import { ChangeTracker } from '@common/prosemirror/changeset/change-tracker';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { resolvedPosForEachNode } from '@common/prosemirror/model/resolved-pos';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { GetTrackedChangeUser, TrackedChangeUser } from '@common/prosemirror/tracked-changes/tracked-change-user.type';
import { getTrackedChangeNodeAttrs, getTrackedChangeUser, TrackedChangeDataOptions, makeTrackedChangeNodeAttrs as trackedChangesMakeTrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-changes';
import { MarkType, ProseMirrorNode, ResolvedPos } from 'prosemirror-model';
import { Command, EditorState, EditorStateConfig, Plugin, PluginKey, Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view';

export const TrackedChangesSetMetaKey: string = 'tracked-changes.set';
export const TrackedChangesIgnoreMetaKey: string = 'tracked-changes.ignore';
export const TrackedChangesTrackedMetaKey: string = 'tracked-changes.tracked';
export const TrackedChangesColorsMetaKey: string = 'tracked-changes.colors';
export const TrackedChangesPluginKey: PluginKey = new PluginKey('tracked-changes');

/**
 * The initial state for the tracked changes plugin.
 */
export interface TrackedChangesPluginOptions {
  /** Whether or not tracked changes are enabled. When enabled tracked changes are applied to edits made by the user. */
  enabled: boolean;
  /** The user making the edits. */
  user: TrackedChangeUser | GetTrackedChangeUser;
}

/**
 * The state of each tracked change color per user
 */
export interface TrackedChangesPluginNewColors {
  /* the colors set on each track change */
  decorations: DecorationSet;
  /* A dictionary to remember which color per user is set */
  cachedColors: Dictionary<number>;
}

/**
 * The tracked changes plugin state.
 */
export interface TrackedChangesPluginState {
  /** The mark type used to create tracked changes around text. */
  readonly changeMarkType: MarkType;
  /** Whether or not tracked changes are enabled. When enabled tracked changes are applied to edits made by the user. */
  readonly enabled: boolean;
  /** The user making the edits. */
  readonly user: TrackedChangeUser | GetTrackedChangeUser;
  /** Create the tracked changes while updating the metadata*/
  makeTrackedChangeNodeAttrs: (changeType: ChangeType, changeData?: TrackedChangeDataOptions) => TrackedChangeNodeAttrs;
  readonly decorations: DecorationSet;
  readonly cachedColors: Dictionary<number>;
  /** Returns the parent change id for the given position if it is the same change type. */
  getParentChangeId: ($pos: ResolvedPos, changeType: ChangeType) => string;
}

/**
 * Returns the tracked changes state from the editor state.
 * @param editorState The editor state to get the tracked changes state from.
 * @returns The tracked changes state.
 */
export function getTrackedChangesState(editorState: EditorState): TrackedChangesPluginState {
  return TrackedChangesPluginKey.getState(editorState);
}

/** Editor command which sets whether track changes is enabled. */
export declare type TrackedChangesSetTrackChangesCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, trackChanges?: boolean) => boolean;

export function setTrackChanges(): TrackedChangesSetTrackChangesCommand {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, trackChanges?: boolean): boolean {
    if (dispatch) {
      const tr = editorState.tr;
      tr.setMeta(TrackedChangesSetMetaKey, {
        enabled: trackChanges
      } as TrackedChangesPluginState);
      dispatch(tr);
    }

    return true;
  }
}

export function toggleTrackChanges(): Command {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    const trackedChangesState: TrackedChangesPluginState = TrackedChangesPluginKey.getState(editorState);

    if (dispatch) {
      const tr = editorState.tr;
      tr.setMeta(TrackedChangesSetMetaKey, {
        enabled: !trackedChangesState?.enabled
      } as TrackedChangesPluginState);
      dispatch(tr);
    }

    return true;
  }
}

/**
 * Returns the color per change, so that it was easier to see who made a change.
 * @param doc The document in question that we editing
 * @param cachedColors The assigned color per user
 * @returns A DecorationSet of things that need a color
 */
export function buildDecorations(doc: ProseMirrorNode, trackedChangesPluginState: TrackedChangesPluginState): TrackedChangesPluginNewColors {
  const decorations: Decoration[] = [];
  let cachedColors: Dictionary<number> = { ...trackedChangesPluginState.cachedColors };

  doc.descendants((node, pos) => {
    const changeNodeAttrs = getTrackedChangeNodeAttrs(trackedChangesPluginState.changeMarkType, node);

    if (changeNodeAttrs) {
      const userName = changeNodeAttrs.changeList?.[0].userName;

      if (userName && !cachedColors[userName]) {
        const cachedColorsAmount = Object.keys(cachedColors).length;
        cachedColors[userName] = (cachedColorsAmount % 10) + 1;
      }

      if (node.isText || node.type.spec.unchangeable) {
        decorations.push(
          Decoration.inline(pos, pos + node.nodeSize, {
            class: `mc-lwe-madcap-change-theme mc-lwe-madcap-change-theme-${cachedColors[userName]}`
          }, { pluginKey: TrackedChangesPluginKey })
        );
      } else {
        decorations.push(
          Decoration.node(pos, pos + node.nodeSize, {
            class: `mc-lwe-madcap-change-theme mc-lwe-madcap-change-theme-${cachedColors[userName]}`
          }, { pluginKey: TrackedChangesPluginKey })
        );
      }
    }
  });

  return { decorations: DecorationSet.create(doc, decorations), cachedColors };
}

/**
 * Returns the tracked change attributes for a DOM element from a set of decorations.
 * This returns the same attributes that are applied to a DOM element by the ProseMirror decoration system.
 * @param decorations The decorations to get the tracked change related DOM attributes from.
 * @returns A dictionary of tracked change related DOM attribute name/value pairs.
 */
export function getTrackedChangesPluginDOMAttrs(decorations: readonly Decoration[]): Dictionary<string> {
  let trackedChangeAttrs: Dictionary<string> = {};

  decorations.forEach((decoration) => {
    // Check that the decoration is from this plugin and that it has a class attribute before trying to add the tracked change class names
    if (decoration.spec?.pluginKey === TrackedChangesPluginKey && (decoration as any).type?.attrs?.class) {
      trackedChangeAttrs = {
        ...trackedChangeAttrs,
        ...(decoration as any).type.attrs
      };
    }
  });

  return trackedChangeAttrs;
}

/**
 * Creates and returns a trackedChanges plugin instance.
 * The plugin state can be retrieved by commands to get the user making the changes and whether tracked changes are enabled.
 * @param options The options for the tracked changes plugin.
 * @returns The tracked changes plugin instance.
 */
export function trackedChangesPlugin(options: TrackedChangesPluginOptions): Plugin {
  let user: TrackedChangeUser | GetTrackedChangeUser = options.user;


  function makeTrackedChangeNodeAttrs(changeType: ChangeType, changeDataOptions?: TrackedChangeDataOptions): TrackedChangeNodeAttrs {
    const changeData = ChangeTracker.metaData.next(changeType);
    return trackedChangesMakeTrackedChangeNodeAttrs(changeType, user, {
      changeId: changeData.id,
      changeTimestamp: changeData.timestamp,
      ...changeDataOptions
    });
  }

  function getParentChangeId($pos: ResolvedPos, changeType: ChangeType, changeMark: MarkType): string {
    let parentChangeId: string;

    const userId = getTrackedChangeUser(options.user)?.Id;

    resolvedPosForEachNode($pos, parent => {
      const trackedAttrs = getTrackedChangeNodeAttrs(changeMark, parent);
      const existingAddFromSameUser = trackedAttrs?.changeList.find(change => change.changeType === changeType && change.creatorCentralUserId === userId);

      if (existingAddFromSameUser) {
        parentChangeId = existingAddFromSameUser.id;
        return false;
      } else {
        return true;
      }
    });

    return parentChangeId;
  }

  return new Plugin({
    key: TrackedChangesPluginKey,

    state: {
      init(config: EditorStateConfig, editorState: EditorState): TrackedChangesPluginState {
        return {
          changeMarkType: (editorState.schema as CollabSchema).changeMark,
          enabled: !!options.enabled,
          user: options.user,
          makeTrackedChangeNodeAttrs: (changeType, changeDataOptions) => makeTrackedChangeNodeAttrs(changeType, changeDataOptions),
          decorations: null,
          cachedColors: {},
          getParentChangeId: ($pos, changeType) => getParentChangeId($pos, changeType, (editorState.schema as CollabSchema).changeMark)
        };
      },
      apply(tr: Transaction, stateValue: TrackedChangesPluginState): TrackedChangesPluginState {
        const trackedChangesConfig: TrackedChangesPluginState = tr.getMeta(TrackedChangesSetMetaKey);
        let newState: TrackedChangesPluginState = stateValue;

        if (tr.getMeta(TrackedChangesColorsMetaKey) || tr.docChanged) {
          newState = {
            ...newState,
            ...buildDecorations(tr.doc, stateValue)
          };
        }
        else if (newState.decorations) {
          newState = {
            ...newState,
            decorations: newState.decorations.map(tr.mapping, tr.doc)
          }
        }

        // set tracked changes config
        if (trackedChangesConfig) {
          newState = {
            ...newState,
            ...trackedChangesConfig
          };
        }

        return newState;
      },

    },
    props: {
      decorations(editorState: EditorState) {
        return this.getState(editorState).decorations;
      }
    },
    appendTransaction(transactions: Transaction[], oldState: EditorState, newState: EditorState): Transaction {
      if ((TrackedChangesPluginKey.getState(newState) as TrackedChangesPluginState)?.enabled) {
        const tr = newState.tr;

        if (Array.isArray(transactions)) {
          transactions.forEach(transaction => {
            // If the doc was changed AND do not ignore AND was not already tracked AND was not a collab transaction AND was not a history transaction AND was not an anno transaction
            if (transaction.docChanged && !transaction.getMeta('trackedChange') && !transaction.getMeta(TrackedChangesIgnoreMetaKey) && !transaction.getMeta(TrackedChangesTrackedMetaKey) && !transaction.getMeta('trackedChangesCleanUp') && !transaction.getMeta('trackedChangesMarkFixer') && !transaction.getMeta('collab$') &&
              !transaction.getMeta('history$') && !transaction.getMeta('anno')) {
              const tracker = new ChangeTracker(transaction, oldState, newState, options.user);
              tracker.applyChange(tr);
            }
          });

          // If any changes were made then return the transaction
          if (tr.docChanged) {
            tr.setMeta('trackedChange', true);
            return tr;
          }
        }
      }
    }
  });
}
